import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './styles.module.scss';
import { Chip } from '../Chip';

type ShopMenu = {
  menu: string;
  price: number;
};

type ShopData = {
  imageUrl: string;
  title: string;
  shopMenuList: ShopMenu[];
  description: string;
  labels: string[];
};

type Props = {
  sectionTitle: string;
  shops: ShopData[];
  className?: string;
  onClose?: () => void;
};

const ShopCard: React.FC<Props> = ({ sectionTitle, shops, className, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const displayShops = shops;

  const handleScroll = useCallback(() => {
    if (!carouselRef.current || shops.length <= 1 || isScrollingRef.current) return;

    const carousel = carouselRef.current;
    const scrollLeft = carousel.scrollLeft;
    const isDesktop = window.innerWidth >= 1024;
    const itemWidth = isDesktop ? 275 : 270;
    const gap = 20;

    const currentPosition = Math.round(scrollLeft / (itemWidth + gap));
    const validIndex = Math.max(0, Math.min(currentPosition, shops.length - 1));
    setCurrentIndex(validIndex);
  }, [shops.length]);

  const moveToNextSlide = useCallback(() => {
    if (!carouselRef.current || shops.length <= 1 || isScrollingRef.current) return;

    const nextIndex = (currentIndex + 1) % shops.length;
    const carousel = carouselRef.current;
    const isDesktop = window.innerWidth >= 1024;
    const itemWidth = isDesktop ? 275 : 270;
    const gap = 20;
    const targetScrollLeft = nextIndex * (itemWidth + gap);

    isScrollingRef.current = true;
    carousel.scrollLeft = targetScrollLeft;
    setCurrentIndex(nextIndex);

    setTimeout(() => {
      isScrollingRef.current = false;
    }, 300);
  }, [currentIndex, shops.length]);

  // 自動スクロールの設定
  useEffect(() => {
    if (shops.length <= 1) return;

    // 自動スクロールを開始
    const startAutoScroll = () => {
      autoScrollIntervalRef.current = setInterval(() => {
        moveToNextSlide();
      }, 3000); // 3秒ごとにスライド
    };

    startAutoScroll();

    // カルーセルにマウスが乗ったら自動スクロールを停止
    const handleMouseEnter = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };

    const handleMouseLeave = () => {
      startAutoScroll();
    };

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('mouseenter', handleMouseEnter);
      carousel.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      if (carousel) {
        carousel.removeEventListener('mouseenter', handleMouseEnter);
        carousel.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [shops.length, moveToNextSlide]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel && shops.length > 1) {
      carousel.style.scrollBehavior = 'auto';
      carousel.scrollLeft = 0;

      setTimeout(() => {
        carousel.style.scrollBehavior = 'smooth';
      }, 100);

      carousel.addEventListener('scroll', handleScroll);

      return () => {
        carousel.removeEventListener('scroll', handleScroll);
      };
    }
  }, [shops.length, handleScroll]);

  const currentShop = shops[currentIndex] || shops[0];

  if (!currentShop) {
    return null;
  }

  return (
    <div className={`${styles.card} ${className || ''}`}>
      {onClose && (
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="閉じる">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M2 2L16 16" stroke="#121212" strokeWidth="2" strokeLinecap="round" />
            <path d="M16 2L2 16" stroke="#121212" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
      {sectionTitle && (
        <div className={styles.header}>
          <h3 className={styles.sectionTitle}>{sectionTitle}</h3>
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.carouselContainer}>
          <div ref={carouselRef} className={styles.imageCarousel}>
            {displayShops.map((shop, index) => (
              <div key={`${shop.title}-${index}`} className={styles.carouselImageWrapper}>
                <div
                  className={styles.carouselImageBg}
                  style={{ backgroundImage: `url(${shop.imageUrl})` }}
                />
                <img src={shop.imageUrl} alt={shop.title} className={styles.carouselImage} />
              </div>
            ))}
          </div>

          {shops.length > 1 && (
            <div className={styles.indicators}>
              {shops.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.indicator} ${index === currentIndex ? styles.indicatorActive : ''}`}
                  onClick={() => {
                    if (carouselRef.current && !isScrollingRef.current) {
                      isScrollingRef.current = true;
                      const carousel = carouselRef.current;
                      const isDesktop = window.innerWidth >= 1024;
                      const itemWidth = isDesktop ? 275 : 270;
                      const gap = 20;
                      const targetScrollLeft = index * (itemWidth + gap);
                      carousel.scrollLeft = targetScrollLeft;
                      setCurrentIndex(index);
                      setTimeout(() => {
                        isScrollingRef.current = false;
                      }, 300);
                    }
                  }}
                  aria-label={`店舗 ${index + 1} を表示`}
                />
              ))}
            </div>
          )}
        </div>

        <div className={styles.scrollableContent}>
          <h4 className={styles.shopTitle}>
            {currentShop.title.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {index > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </h4>

          <div className={styles.separator} />

          {/* Menu List Section */}
          <div className={styles.menuList}>
            {currentShop.shopMenuList.map((item, index) => (
              <div key={index} className={styles.menuItem}>
                <span className={styles.menuItem__name}>{item.menu}</span>
                <span className={styles.menuItem__price}>¥{item.price}</span>
              </div>
            ))}
          </div>

          <div
            className={styles.description}
            dangerouslySetInnerHTML={{ __html: currentShop.description }}
          />
        </div>

        {/* Chips Section - スクロール領域の外 */}
        <div className={styles.chips}>
          {currentShop.labels.map((label) => (
            <Chip key={label} text={label} variant="primary" />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShopCard;
